import { makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import { type SourceKind } from '@/domain/beforered';
import { InvariantError } from '@/domain/errors';
import { type ImportSlotFactory, type ImportSlotStore } from './transient/ImportSlotStore';

const ALL_SOURCES: readonly SourceKind[] = [
  'BOM',
  'INVENTORY',
  'SALES',
  'MFG',
  'PURCHASE',
  'PARTS',
];

export class ImportsStore {
  private _slots: Map<SourceKind, ImportSlotStore> = new Map();

  constructor(makeSlot: ImportSlotFactory) {
    makeAutoObservable(this);
    for (const source of ALL_SOURCES) {
      const slot = makeSlot();
      slot.init(source);
      this._slots.set(source, slot);
    }
  }

  slot = (source: SourceKind): ImportSlotStore => {
    const s = this._slots.get(source);
    if (!s) throw new InvariantError(`No slot for source ${source}`);
    return s;
  };

  get bom(): ImportSlotStore {
    return this.slot('BOM');
  }
  get inventory(): ImportSlotStore {
    return this.slot('INVENTORY');
  }
  get sales(): ImportSlotStore {
    return this.slot('SALES');
  }
  get mfg(): ImportSlotStore {
    return this.slot('MFG');
  }
  get purchase(): ImportSlotStore {
    return this.slot('PURCHASE');
  }
  get parts(): ImportSlotStore {
    return this.slot('PARTS');
  }

  get hasDemandSource(): boolean {
    return this.sales.isValid || this.mfg.isValid;
  }

  get isReadyToRun(): boolean {
    // BOM + a demand source are the structural minimum. Inventory missing = greenfield
    // (assume zero on-hand). Parts missing = global lead-time defaults + SKU as description.
    return this.bom.isValid && this.hasDemandSource;
  }
}

export const TOKEN_ImportsStore = token<ImportsStore>('ImportsStore');
